import {
  TmElement,
  color,
  radius,
  reset,
  spacing,
  token,
} from '@thiememeulenhoff/lit-sdk';
import { css, html } from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import { tagName } from '../../helpers';
import { ToggleButtonComponent } from './toggle-button/toggle-button.component';

const tag = tagName('toggle-group');

@customElement(tag)
export class ToggleGroupComponent extends TmElement {
  static override styles = [
    css`
      :where(*) {
        box-sizing: border-box;
      }

      :host {
        display: inline-flex;
        flex-direction: column;
        gap: ${spacing(100)};
      }

      :where(.vertical) {
        flex-direction: column;
      }

      :where(.toggle-container) {
        background-color: ${token('background-tertiary')};
        border-radius: ${radius('special')};
        display: flex;
        font-family: sans-serif;
        inline-size: 100%;
        padding: ${spacing(25)};
        position: relative;

        :where(.horizontal) {
          gap: ${spacing(100)};
        }
      }

      :where(.button-background) {
        background-color: var(
          --tm-toggle-group-selected-background,
          var(--current-button-color, ${token('background-secondary')})
        );
        block-size: var(--current-button-block-size, 0);
        border-radius: ${radius('medium')};
        inline-size: var(--current-button-inline-size, 0);
        inset-block-start: var(--current-button-top, 2);
        inset-inline-start: var(--current-button-left, 2);
        position: absolute;
        transition:
          0.4s inset-block-start,
          0.4s inset-inline-start,
          0.4s background-color;
      }

      ::slotted(*) {
        position: relative;
      }

      :host([fill]) {
        display: flex;
      }
    `,
    reset.button,
  ];

  @property({ type: String, reflect: true })
  orientation: 'horizontal' | 'vertical' = 'horizontal';

  #value = '';

  @property({ type: String, reflect: true })
  set value(value: string) {
    this.#value = value;

    this.assignedElements.forEach((element) => {
      if (element.value === this.value && !element.disabled) {
        element.setActive(true);
        this.#setActiveStateStyling(element);
      } else {
        element.setActive(false);
      }
    });
  }

  get value() {
    return this.#value;
  }

  @queryAssignedElements({ selector: 'a-toggle-button' })
  assignedElements!: Array<ToggleButtonComponent>;

  #resizeObserver = new ResizeObserver(() => {
    if (this.activeElement) {
      this.#setActiveStateStyling(this.activeElement);
    }
  });

  protected override onCreate = async () => {
    this.listen('toggle-button-selected', this.#onToggleButtonSelected);
    this.#resizeObserver.observe(this);

    await this.updateComplete;
    this.#setInitialActiveState();
  };

  override render() {
    return html`
      <div class="toggle-container ${this.orientation}" part="container">
        <div class="button-background"></div>
        <slot></slot>
      </div>
    `;
  }

  get activeElement() {
    return this.assignedElements.find((element) => element.active);
  }

  #setInitialActiveState() {
    if (this.#value === '') {
      this.#value =
        this.assignedElements.find((element) => !element.disabled)?.value || '';
    }

    this.assignedElements.forEach((element) => {
      if (element.value === this.value && !element.disabled) {
        element.setActive(true);
        this.#setActiveStateStyling(element);
      }
    });
  }

  #setActiveStateStyling(target: ToggleButtonComponent) {
    this.style.setProperty(
      '--current-button-block-size',
      `${target.getBoundingClientRect().height}px`,
    );
    this.style.setProperty(
      '--current-button-inline-size',
      `${target.getBoundingClientRect().width}px`,
    );
    this.style.setProperty(
      '--current-button-top',
      `${target.getBoundingClientRect().top - this.getBoundingClientRect().top}px`,
    );
    this.style.setProperty(
      '--current-button-left',
      `${target.getBoundingClientRect().left - this.getBoundingClientRect().left}px`,
    );
    this.style.setProperty(
      '--current-button-color',
      color(target.color, 600).cssText,
    );
  }

  #onToggleButtonSelected = (event: Event) => {
    const target = event.target as ToggleButtonComponent;

    if (target?.active) {
      return;
    }

    const updatedValue = (event as CustomEvent).detail;
    const changeEvent = this.dispatchLocalEvent({
      eventName: 'change',
      detail: updatedValue,
      cancelable: true,
    });

    if (changeEvent.defaultPrevented) {
      return;
    }

    this.#value = updatedValue;
    this.#setActiveStateStyling(target);

    this.assignedElements.forEach((element) => {
      element.setActive(target === element);
    });
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-toggle-group': ToggleGroupComponent;
  }
}
